/**
 * 全局权限控制指令
 * 用于控制按钮等元素的显示状态
 * 
 * 使用方式:
 * 1. v-permission="['admin']" - 单个权限
 * 2. v-permission="['admin', 'user']" - 多个权限(满足其一即可)
 * 3. v-permission.all="['admin', 'user']" - 多个权限(需全部满足)
 * 4. v-if-permission="'admin'" - 条件渲染(不占用DOM)
 */

import store from '@/store'

/**
 * 检查是否有权限
 * @param {Array} value - 需要的权限数组
 * @param {Boolean} requireAll - 是否需要全部权限
 * @returns {Boolean}
 */
function checkPermission(value, requireAll = false) {
  if (value && value instanceof Array && value.length > 0) {
    const permissions = store.getters && store.getters.permissions
    const requiredPermissions = value

    if (!permissions || permissions.length === 0) {
      return false
    }

    if (requireAll) {
      // 需要拥有全部权限
      return requiredPermissions.every(permission => permissions.includes(permission))
    } else {
      // 只需拥有其中一个权限
      return requiredPermissions.some(permission => permissions.includes(permission))
    }
  } else {
    console.error('需要传入权限数组! 例如: v-permission="[\'admin\',\'editor\']"')
    return false
  }
}

/**
 * 权限指令 - 控制元素显示隐藏(保留DOM)
 */
export const permission = {
  inserted(el, binding) {
    const { value, modifiers } = binding
    const requireAll = modifiers.all || false
    
 
    if (!checkPermission(value, requireAll)) {
      el.style.display = 'none'
      el.setAttribute('data-permission-hidden', 'true')
    }
  },
  update(el, binding) {
    const { value, modifiers } = binding
    const requireAll = modifiers.all || false
    
    if (!checkPermission(value, requireAll)) {
      el.style.display = 'none'
      el.setAttribute('data-permission-hidden', 'true')
    } else {
      el.style.display = ''
      el.removeAttribute('data-permission-hidden')
    }
  }
}

/**
 * 权限指令 - 条件渲染(移除DOM)
 */
export const ifPermission = {
  inserted(el, binding) {
    const { value, modifiers } = binding
    const requireAll = modifiers.all || false
    
    if (!checkPermission(value, requireAll)) {
      el.parentNode && el.parentNode.removeChild(el)
    }
  }
}

/**
 * 角色指令 - 基于角色控制显示
 */
export const role = {
  inserted(el, binding) {
    const { value } = binding
    const roles = store.getters && store.getters.roles

    if (value && value instanceof Array && value.length > 0) {
      const requiredRoles = value
      const hasRole = roles.some(role => requiredRoles.includes(role))

      if (!hasRole) {
        el.style.display = 'none'
        el.setAttribute('data-role-hidden', 'true')
      }
    } else {
      console.error('需要传入角色数组! 例如: v-role="[\'admin\',\'editor\']"')
    }
  },
  update(el, binding) {
    const { value } = binding
    const roles = store.getters && store.getters.roles

    if (value && value instanceof Array && value.length > 0) {
      const requiredRoles = value
      const hasRole = roles.some(role => requiredRoles.includes(role))

      if (!hasRole) {
        el.style.display = 'none'
        el.setAttribute('data-role-hidden', 'true')
      } else {
        el.style.display = ''
        el.removeAttribute('data-role-hidden')
      }
    }
  }
}

/**
 * 按钮权限指令 - 专门用于按钮的权限控制
 * 支持禁用和隐藏两种模式
 */
export const btnPermission = {
  inserted(el, binding) {
    const { value, modifiers } = binding
    const requireAll = modifiers.all || false
    const disableMode = modifiers.disable || false // 使用 v-btn-permission.disable 来禁用而非隐藏
    
    if (!checkPermission(value, requireAll)) {
      if (disableMode) {
        el.disabled = true
        el.classList.add('is-disabled')
        el.setAttribute('data-permission-disabled', 'true')
      } else {
        el.style.display = 'none'
        el.setAttribute('data-permission-hidden', 'true')
      }
    }
  },
  update(el, binding) {
    const { value, modifiers } = binding
    const requireAll = modifiers.all || false
    const disableMode = modifiers.disable || false
    
    if (!checkPermission(value, requireAll)) {
      if (disableMode) {
        el.disabled = true
        el.classList.add('is-disabled')
        el.setAttribute('data-permission-disabled', 'true')
      } else {
        el.style.display = 'none'
        el.setAttribute('data-permission-hidden', 'true')
      }
    } else {
      if (disableMode) {
        el.disabled = false
        el.classList.remove('is-disabled')
        el.removeAttribute('data-permission-disabled')
      } else {
        el.style.display = ''
        el.removeAttribute('data-permission-hidden')
      }
    }
  }
}

// 导出检查权限的工具函数,供JS代码使用
export { checkPermission }


// WEBPACK FOOTER //
// ./src/directive/permission.js